import { useMemo, useEffect, useState } from "react";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { Formik, FormikHelpers } from "formik";
import { BEModulesClassNames } from "@utils/moduleList";
import { ModuleFull } from "./processingTypes";
import { useContextAwareGetProcessing, useUpdateProcessingsMutation, useContextAwareGetProcessingEntries, useUpdateProcessingEntryMutation } from "@hooks/index";
import { getSchemas, validationSchema, initialValues, waterUseNullData } from "./processing";
import { FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import ProcessingTierOne from "./ProcessingTierOne";
import GenericTierTwo from "../GenericTierTwo";

const ProcessingModule = () => {
  const { activeActivityId } = useAppSelector(selectCurrentProject);
  const { moduleList } = useAppSelector(selectCurrentBuilder);
  const id = moduleList.find((module) => module.id === FEModules.Processing)?.uniqueId;

  const { data: processingsData, isLoading, isError, error } = useContextAwareGetProcessing(id ?? 0, { refetchOnMountOrArgChange: true });
  const { data: entriesData, isLoading: isEntriesLoading, isError: isEntriesError, error: entriesError } = useContextAwareGetProcessingEntries({ activityId: activeActivityId ?? 0 });
  const [updateProcessings, { isError: isUpdateError, error: updateError }] = useUpdateProcessingsMutation();
  const [updateProcessingEntry, { isError: isUpdateProcessingEntryError, error: updateProcessingEntryError, isLoading: isUpdateProcessingEntryLoading }] = useUpdateProcessingEntryMutation();

  const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
  const [initValues, setInitValues] = useState<ModuleFull>(initialValues);
  const { errorMsg: updateParentErrorMsg } = useModuleHook({
    skeletonsLoadingDeps: [isLoading, isEntriesLoading],
    isMutateError: isUpdateError,
    mutateError: updateError,
  });

  const { errorMsg: fetchError } = useErrorMessage({ isError: isEntriesError ?? isError, error: entriesError ?? error });
  const { errorMsg: updateEntryErrorMsg } = useErrorMessage({ isError: isUpdateProcessingEntryError, error: updateProcessingEntryError });
  const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

  useEffect(() => {
    if (!processingsData) return
    setInitValues((cur) => ({ ...cur, parent: processingsData }))
  }, [processingsData])

  useEffect(() => {
    if (entriesData)
      processModuleThreads({ processings: entriesData })
  }, [processModuleThreads, entriesData])

  const handleSubmit = async (
    values: ModuleFull,
    { resetForm }: FormikHelpers<ModuleFull>
  ) => {
    const { processings, parent } = values
    try {
      const promises: Promise<any>[] = []
      const parentPayload = { body: { ...parent }, activityId: activeActivityId ?? 0 }

      processings.map((entry) => {
        const payload = { body: entry.is_water_used ? { ...entry } : { ...entry, ...waterUseNullData }, activityId: activeActivityId ?? 0 }
        if (entry.id) promises.push(updateProcessingEntry(payload))
        return Promise.resolve()
      })

      await Promise.all(promises);
      await new Promise(resolve => setTimeout(resolve, 500));
      if (parent.id) updateProcessings(parentPayload)
      resetForm({ values })
    } catch (error) {
      console.error(error)
    }
  }

  return <Formik initialValues={initValues} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize>
    <>
      <ProcessingTierOne
        title="project.processing"
        initValues={initValues}
        t2Schema={t2Schema}
        beModuleType={BEModulesClassNames.Processing}
        moduleSchema={moduleSchema}
        isLoading={isLoading || isEntriesLoading || isUpdateProcessingEntryLoading}
        isEntriesLoading={isEntriesLoading}
        footerError={updateParentErrorMsg ?? updateEntryErrorMsg}
        blockingError={fetchError}
        setInitValues={setInitValues}
      />
      <GenericTierTwo
        title="project.processing"
        t2Schema={t2Schema}
      />
    </>
  </Formik>
};

export default ProcessingModule;